/**
 * Generation provenance — the three marks (scenarios doc §4).
 *
 * 2026-08-07 measured: ~60% of generation decisions are model guesses, and
 * where the spec speaks the pipeline is deterministic. The bank developer's
 * trust axis is seeing WHICH is which. Every surfaced item carries one of:
 *
 *   - 선언에서 파생   — the user's own document ruled it (intake decisions)
 *   - 표준 기본값     — a verified corpus example / standard supplied it
 *   - 제가 골랐습니다 — the model guessed because the document was silent
 *
 * plus the "당신에게 물어야 하는 것" list in the §4-measured priority order
 * (named computed values → named interactions → display form → area layout).
 * The §4 prohibition is honored: NEVER ask for full element trees or label
 * dictionaries — that hands authorship back to the user.
 *
 * Pure derivation over signals the pipeline actually recorded — nothing here
 * is inferred from content, so no bucket can overclaim.
 */
import type { IntakeDecisions } from '@cogo/agentic-generator/multiblock/intake-decisions'
import { validateIntakeDecisions } from '@cogo/agentic-generator/multiblock/intake-decisions'
import type { RenderedProvenance } from '@/features/_shared/channel-types'

export interface GenerationProvenanceView {
  declared: string[]
  standard: string[]
  guessed: string[]
  askNext: string[]
}

/**
 * Spec-field provenance (§7-2 display seat). The spec-completeness plan's
 * Task B7 records, per spec field, WHO supplied the value:
 *   user-stated / agent-proposed / library-default.
 * Studio is the display side of that contract. Until B7 writes the data the
 * reader returns null and the card shows nothing — the codebase rule is
 * "never an empty overclaim", so the seat is wired but silent.
 */
export type SpecFieldOrigin = 'user-stated' | 'agent-proposed' | 'library-default'

export interface SpecFieldProvenanceEntry {
  path: string
  origin: SpecFieldOrigin
  note?: string
}

export interface SpecFieldProvenanceView {
  entries: SpecFieldProvenanceEntry[]
  counts: Record<SpecFieldOrigin, number>
}

const SPEC_FIELD_ORIGINS: readonly SpecFieldOrigin[] = [
  'user-stated',
  'agent-proposed',
  'library-default',
]

/** settings.specFieldProvenance is untyped at the store boundary — revalidate. */
export function readSpecFieldProvenanceFromSettings(
  settings: unknown,
): SpecFieldProvenanceView | null {
  if (!settings || typeof settings !== 'object') return null
  const raw = (settings as Record<string, unknown>).specFieldProvenance
  if (!raw || typeof raw !== 'object') return null
  const fields = (raw as Record<string, unknown>).fields
  if (!Array.isArray(fields)) return null
  const entries: SpecFieldProvenanceEntry[] = []
  for (const candidate of fields) {
    if (!candidate || typeof candidate !== 'object') return null
    const path = (candidate as Record<string, unknown>).path
    const origin = (candidate as Record<string, unknown>).origin
    const note = (candidate as Record<string, unknown>).note
    if (typeof path !== 'string' || path.trim() === '') return null
    if (
      typeof origin !== 'string' ||
      !SPEC_FIELD_ORIGINS.includes(origin as SpecFieldOrigin)
    ) {
      return null
    }
    if (note !== undefined && typeof note !== 'string') return null
    entries.push({
      path,
      origin: origin as SpecFieldOrigin,
      ...(typeof note === 'string' ? { note } : {}),
    })
  }
  if (entries.length === 0) return null
  const counts: Record<SpecFieldOrigin, number> = {
    'user-stated': 0,
    'agent-proposed': 0,
    'library-default': 0,
  }
  for (const entry of entries) counts[entry.origin] += 1
  return { entries, counts }
}

/** cexample:foo-bar / https://…/example/foo-bar → foo-bar */
function exampleTail(id: string | undefined): string | null {
  if (!id) return null
  const parts = id.split(/[/#:]/).filter(Boolean)
  return parts.length ? parts[parts.length - 1] : null
}

/** settings.intakeDecisions is untyped at the store boundary — revalidate. */
export function readIntakeDecisionsFromSettings(
  settings: unknown,
): IntakeDecisions | null {
  if (!settings || typeof settings !== 'object') return null
  const raw = (settings as Record<string, unknown>).intakeDecisions
  if (!raw || typeof raw !== 'object') return null
  return validateIntakeDecisions(raw)
}

export function deriveGenerationProvenance(input: {
  provenance?: RenderedProvenance | null
  intakeDecisions?: IntakeDecisions | null
}): GenerationProvenanceView {
  const declared: string[] = []
  const standard: string[] = []
  const guessed: string[] = []
  const askNext: string[] = []

  const decisions = input.intakeDecisions ?? null
  const fv = decisions?.fieldVisibility
  if (fv) {
    const visible = (['input', 'output', 'common', 'unknown'] as const).flatMap(
      (section) => fv.visible[section] ?? [],
    )
    if (visible.length > 0) {
      declared.push(`화면 노출 필드: ${visible.join(', ')}`)
    }
    if (fv.internal.length > 0) {
      declared.push(`내부 전달만(화면 미노출): ${fv.internal.join(', ')}`)
    }
  }
  const lc = decisions?.legacyConversion
  if (lc) {
    if (lc.keep.length > 0) declared.push(`레거시 그대로 유지: ${lc.keep.join(', ')}`)
    if (lc.improve.length > 0) declared.push(`레거시 개선 적용: ${lc.improve.join(', ')}`)
  }
  const sd = decisions?.screenDesign
  if (sd) {
    if (sd.labelsVerbatim.length > 0) {
      declared.push(`설계서 인용 라벨(그대로 사용): ${sd.labelsVerbatim.join(', ')}`)
    }
  }
  const re = decisions?.referenceExtraction
  if (re) {
    if (re.standardTerms.length > 0) {
      declared.push(`표준 용어로 채택: ${re.standardTerms.join(', ')}`)
    }
    if (re.excludedScreenSpecific.length > 0) {
      declared.push(`참조 화면 전용이라 제외: ${re.excludedScreenSpecific.join(', ')}`)
    }
  }

  const provenance = input.provenance ?? null
  if (provenance?.generationSource === 'retrieval') {
    const tail = exampleTail(provenance.exampleId)
    standard.push(
      tail
        ? `화면 구성 — 검증된 표준 예제(${tail})에서 가져왔습니다`
        : '화면 구성 — 검증된 표준 예제에서 가져왔습니다',
    )
  } else if (provenance?.generationSource === 'zero-shot') {
    guessed.push('화면 구성 — 문서에 없어서 제가 골랐습니다')
  }

  // "당신에게 물어야 하는 것" — §4 priority, real signals only, capped.
  if (sd && sd.dataSource === 'undeclared-screen-first') {
    askNext.push('데이터 원천이 아직 선언되지 않았습니다 — 이 화면을 무엇으로 채울지 정해 주세요')
  }
  if (provenance?.generationSource === 'zero-shot') {
    askNext.push('명명된 상호작용 — 무엇을 누르면 무엇이 바뀌어야 하는지 확인해 주세요')
    if (!fv) {
      askNext.push('표시 형태 — 표/카드 중 무엇이 맞는지 확인해 주세요')
    }
  }

  return { declared, standard, guessed, askNext: askNext.slice(0, 4) }
}

export function hasProvenanceContent(view: GenerationProvenanceView): boolean {
  return (
    view.declared.length > 0 ||
    view.standard.length > 0 ||
    view.guessed.length > 0 ||
    view.askNext.length > 0
  )
}
